import { Injectable } from '@angular/core';
import { parseISO } from 'date-fns';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { INotificationFiltersDto, INotificationsResultDto, NOTIFICATIONS } from 'src/app/contracts';
import { Api } from 'src/app/core/common/api';
import { IApiOptions } from 'src/app/core/contracts/core.types';
import { UI } from 'src/app/core/helpers/ui';
import { Auth } from '../core/common/auth';
import { Logger } from '../core/helpers/logger';
import { removeStorageItem } from '../core/helpers/storage';
import { toHttpParams } from '../core/helpers/utils';

@Injectable({ providedIn: 'root' })
export class NotificationsData {
  public newNotificationsCount = new BehaviorSubject<number>(0);
  constructor(private api: Api, private ui: UI, private auth: Auth, private logger: Logger) {
    this.auth.isAuthenticated$.subscribe(isAuthenticated => {
      if (!isAuthenticated) {
        this.newNotificationsCount.next(0);
      }
    });
  }
  public get newCount$() {
    return this.newNotificationsCount.asObservable().pipe(map(count => (count < 100 ? count : 99)));
  }
  public getNotifications(page: number, pageSize?: number, options?: IApiOptions): Observable<INotificationsResultDto> {
    options = { ...options, params: toHttpParams({ page, pageSize } as INotificationFiltersDto) };
    return this.api.get<INotificationsResultDto>('notifications', options).pipe(
      tap((result: INotificationsResultDto) => {
        this.newNotificationsCount.next(result.newNotificationsTotal);
        for (const notification of result.notifications) {
          notification.formattedDate = this.ui.formatDate(parseISO(notification.createdAt as string), 'HH:mm dd/MMM/yyyy');
        }
      })
    );
  }
  public async getNewNotificationsCount(forceUpdate?: boolean) {
    if (!(await this.auth.isAuthenticated())) {
      return Promise.resolve(0);
    }
    return new Promise(resolve => {
      if (forceUpdate) {
        this.api
          .get<number>('notifications/new-notifications-count', { handleError: false, handleLoading: false })
          .pipe(
            catchError(err => {
              resolve(this.newNotificationsCount.getValue());
              return throwError(() => err as unknown);
            })
          )
          .subscribe((newCount: number) => {
            this.newNotificationsCount.next(newCount);
            resolve(newCount);
          });
      } else {
        resolve(this.newNotificationsCount.getValue());
      }
    });
  }
  public dispose() {
    return removeStorageItem(NOTIFICATIONS)
      .catch(err => this.logger.error({ msg: '@notifications.data: Failed to clear notifications from storage', err }))
      .then(() => this.newNotificationsCount.next(0));
  }
}
